import React, { useState, useEffect, useContext } from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import { WebinarPropType } from 'models/types/webinars';
import { getWebinarTimeRange } from 'utils/dateFormatters';
import { WebinarType, selectAllRelevantTracks } from 'models/webinars';
import { ButtonIcon, RoundedButtonLink } from 'components/input/RoundedButton';
import QuillRichtextDisplay from 'components/QuillRichtextDisplay';
import { useSelector } from 'react-redux';
import { UserSessionContext } from 'components/UserSession';
import { isZoomLink } from 'utils/streamableLinkUtils';
import { selectLocale } from 'models/localization';
import WebinarSpeakers from './WebinarSpeakers';
import WebinarMedia from './WebinarMedia';
import Video from '../../components/icons/Video';
import WebinarTrackTag from './WebinarTrackTag';

const WebinarTitle = styled.h1`
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 24px;
  color: #484848;
`;

const WebinarTime = styled.div`
  font-size: 14px;
  color: #A2AAB3;
`;

const ContentContainer = styled.div`
  flex: 1 1;
`;

const WebinarDetails = styled.div`
  font-size: 16px;
  line-height: 22px;
  color: #484848;
  word-break: break-word;
  overflow-x: hidden;

  & .ql-editor {
    padding: 0;
    margin-top: 8px;
  }
`;

const WatchNowButtonFrame = styled.div`
  width: 100%;
  bottom: 0;
  left: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  @media (max-width: 959px) {
    position: fixed;
    padding: 10px 16px;
    z-index: 1;
    border-top: 1px solid rgba(162, 170, 179, 0.3);
    background: white;
  }
`;

const DetailWatchNowButton = styled(RoundedButtonLink)`
  height: 40px;
  background: ${({ color }) => color};
  padding: 0 15px 0 10px;
  font-weight: bold;
  font-size: 16px;
  line-height: 16px;
  width: 100%;
  text-transform: uppercase;
  opacity: ${({ disabled }) => (disabled ? '0.4' : 'initial')};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  & path {
    fill: white;
  }

  & ${ButtonIcon} {
    padding: 0 6px;
    flex-basis: auto;
  }

  @media (min-width: 960px) {
    width: 200px;
    margin-top: 26px;
  }
`;

const WebinarExtraInfoWrapper = styled.div`
  background-color: ${({ hasAuditorium }) => (hasAuditorium ? 'white' : 'initial')};
  padding: 32px;

  @media (max-width: 960px) {
    padding: 16px;
    padding-bottom: 64px;
  }
`;

const WebinarMainContentContainer = ({ webinar }) => {
  const { t } = useTranslation();
  const webinarTimeRange = !webinar.startAt ? null : getWebinarTimeRange(webinar.startAt, webinar.endAt);
  const { startAt, endAt } = webinar;
  const { userSession } = useContext(UserSessionContext);
  const { themeColor, features } = useSelector((s) => s.eventConfigs);
  const trackIds = useSelector(selectAllRelevantTracks()).map((track) => track.id);
  const locale = useSelector(selectLocale);
  const [isComingSoon, setIsComingSoon] = useState(true);
  const [isEnded, setIsEnded] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const hasAuditorium = features.includes('auditoriumWebinar');

  useEffect(() => {
    const updateButton = () => {
      const currentTime = moment();
      setIsComingSoon(currentTime.isBefore(startAt));
      setIsEnded(currentTime.isAfter(endAt));
      setIsLive(!currentTime.isBefore(startAt) && !currentTime.isAfter(endAt));
    };
    updateButton();
    const taskId = setInterval(() => updateButton(), 1000);
    return () => clearInterval(taskId);
  }, [startAt, endAt]);

  const clickWatchNowButton = () => {
    window.trackingItemToFirebase(userSession, 'Webinar', 'PlayWebinarVideo', isLive ? 'isLive' : 'onDemand', webinar.url || '', webinar.id, webinar?.translations[locale]?.title);
  };

  return (
    <ContentContainer id="webinar-main-content-container">
      {(isLive || isEnded) && <WebinarMedia webinar={webinar} isLive={isLive} />}
      <WebinarExtraInfoWrapper hasAuditorium={hasAuditorium} id="webinar-extra-info-wrapper">
        <WebinarTitle id="webinar-title">{webinar.translations[locale]?.title}</WebinarTitle>
        <WebinarTime id="webinar-time">
          {webinarTimeRange && t(
            'webinar.datetime_format',
            `${webinarTimeRange.from} - ${webinarTimeRange.to} (${webinarTimeRange.length}mins) ${webinarTimeRange.date}`,
            {
              startTime: webinarTimeRange.from,
              endTime: webinarTimeRange.to,
              duration: webinarTimeRange.length,
              timezone: webinarTimeRange.timezone,
              date: webinarTimeRange.date,
            },
          )}
        </WebinarTime>
        {webinar.track && trackIds.includes(webinar.track.id) && <WebinarTrackTag track={webinar.track} />}
        {(webinar.kind === WebinarType.Live && (webinar.translations[locale]?.url?.trim()?.length ?? 0) > 0) && (
          <WatchNowButtonFrame disabled={isEnded || isComingSoon}>
            <DetailWatchNowButton
              id="webinar-watch-now-button"
              kind={webinar.kind}
              onClick={isLive ? clickWatchNowButton : undefined}
              href={isLive ? webinar.translations?.[locale]?.url : undefined}
              target="_blank"
              disabled={isEnded || isComingSoon}
              color={themeColor || DEFAULT_THEME_COLOR}
            >
              <ButtonIcon><Video /></ButtonIcon>
              {isLive && (isZoomLink(webinar?.translations?.[locale]?.url) ? t('webinar.open_zoom', 'Open with Zoom') : t('webinar.session.live', 'Watch Now'))}
              {isEnded && t('webinar.session.ended', 'Session Ended')}
              {isComingSoon && t('webinar.session.coming_soon', 'Coming Soon')}
            </DetailWatchNowButton>
          </WatchNowButtonFrame>
        )}

        <WebinarDetails id="webinar-details">
          <QuillRichtextDisplay html={webinar.translations[locale]?.description} />
        </WebinarDetails>
        {webinar.speakers && webinar.speakers.length > 0 && <WebinarSpeakers webinar={webinar} />}
      </WebinarExtraInfoWrapper>
    </ContentContainer>
  );
};

WebinarMainContentContainer.propTypes = { webinar: WebinarPropType.isRequired };

export default WebinarMainContentContainer;
